"use client";

import * as React from "react";
import { cn } from "@/lib/utils";

export interface InputProps
  extends Omit<React.ComponentProps<"input">, "size"> {
  /** Label text displayed above the input */
  label?: string;
  /** Helper text displayed below the input */
  helperText?: string;
  /** When true, shows "(optional)" after the label. Required is the default. */
  optional?: boolean;
  /** Icon displayed on the left side of the input */
  leadingIcon?: React.ReactNode;
  /** Icon displayed on the right side of the input */
  trailingIcon?: React.ReactNode;
  /** Text displayed inside the input on the right (e.g. "days", "kg"). Display-only, not sent to backend. */
  suffix?: string;
  /** When true, displays error styling */
  isError?: boolean;
  /** Error message that replaces helperText when isError is true */
  errorMessage?: string;
  /** Size variant of the input */
  size?: "sm" | "md" | "lg";
  /** Additional class name for the wrapper element */
  wrapperClassName?: string;
  /** Show a character counter when maxLength is set. Defaults to false. */
  showCharacterCount?: boolean;
  /** When true, shows error/helper text and counter on one row. */
  inlineMeta?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = "text",
      label,
      helperText,
      optional = false,
      leadingIcon,
      trailingIcon,
      suffix,
      isError = false,
      errorMessage,
      size = "md",
      disabled,
      readOnly,
      id,
      wrapperClassName,
      showCharacterCount = false,
      inlineMeta = false,
      maxLength,
      value,
      ...props
    },
    ref
  ) => {
    // Generate a unique ID for accessibility if not provided
    const generatedId = React.useId();
    const inputId = id || generatedId;
    const helperId = `${inputId}-helper`;
    const hasHelperOrError = !!(helperText || (isError && errorMessage));
    
    // Non-editable state (disabled or readOnly)
    const isNonEditable = disabled || readOnly;

    // Character count logic
    const charCount = typeof value === "string" ? value.length : 0;
    const showCounter = showCharacterCount && maxLength != null;
    const charRatio = maxLength ? charCount / maxLength : 0;
    const counterColor =
      charRatio >= 1
        ? "text-error-700 font-medium"
        : charRatio >= 0.9
          ? "text-warning-700"
          : "text-text-secondary";

    // Size-specific classes
    const sizeClasses = {
      sm: {
        input: "py-(--input-sm-padding-y) body-xs-regular",
        paddingX: "px-(--input-sm-padding-x)",
        paddingL: "pl-(--input-sm-padding-x)",
        paddingR: "pr-(--input-sm-padding-x)",
        paddingLeftWithIcon: "pl-[calc(var(--input-sm-padding-x)+var(--input-sm-icon-size)+var(--input-sm-gap))]",
        paddingRightWithIcon: "pr-[calc(var(--input-sm-padding-x)+var(--input-sm-icon-size)+var(--input-sm-gap))]",
        icon: "size-[var(--input-sm-icon-size)]",
        iconLeft: "left-(--input-sm-padding-x)",
        iconRight: "right-(--input-sm-padding-x)",
      },
      md: {
        input: "py-(--input-md-padding-y) body-sm-regular",
        paddingX: "px-(--input-md-padding-x)",
        paddingL: "pl-(--input-md-padding-x)",
        paddingR: "pr-(--input-md-padding-x)",
        paddingLeftWithIcon: "pl-[calc(var(--input-md-padding-x)+var(--input-md-icon-size)+var(--input-md-gap))]",
        paddingRightWithIcon: "pr-[calc(var(--input-md-padding-x)+var(--input-md-icon-size)+var(--input-md-gap))]",
        icon: "size-[var(--input-md-icon-size)]",
        iconLeft: "left-(--input-md-padding-x)",
        iconRight: "right-(--input-md-padding-x)",
      },
      lg: {
        input: "py-(--input-lg-padding-y) body-md-regular",
        paddingX: "px-(--input-lg-padding-x)",
        paddingL: "pl-(--input-lg-padding-x)",
        paddingR: "pr-(--input-lg-padding-x)",
        paddingLeftWithIcon: "pl-[calc(var(--input-lg-padding-x)+var(--input-lg-icon-size)+var(--input-lg-gap))]",
        paddingRightWithIcon: "pr-[calc(var(--input-lg-padding-x)+var(--input-lg-icon-size)+var(--input-lg-gap))]",
        icon: "size-[var(--input-lg-icon-size)]",
        iconLeft: "left-(--input-lg-padding-x)",
        iconRight: "right-(--input-lg-padding-x)",
      },
    };

    const currentSize = sizeClasses[size];

    const hasSuffix = !!(suffix && !trailingIcon);

    // Determine padding based on icons (using static classes for Tailwind JIT)
    // Suffix is a flex sibling, not an overlay, so it doesn't affect input padding.
    const paddingClasses = cn(
      !leadingIcon && !trailingIcon && currentSize.paddingX,
      leadingIcon && !trailingIcon && cn(currentSize.paddingLeftWithIcon, currentSize.paddingR),
      !leadingIcon && trailingIcon && cn(currentSize.paddingL, currentSize.paddingRightWithIcon),
      leadingIcon && trailingIcon && cn(currentSize.paddingLeftWithIcon, currentSize.paddingRightWithIcon)
    );

    return (
      <div className={cn("flex flex-col gap-1", wrapperClassName)}>
        {/* Label */}
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              "body-sm-medium text-(--input-label)",
              disabled && "opacity-50"
            )}
          >
            {label}
            {optional && (
              <span className="text-(--input-label-optional) body-sm-regular ml-1">
                (optional)
              </span>
            )}
          </label>
        )}

        {/* Input container */}
        <div className={cn(
          "relative",
          isNonEditable && "cursor-not-allowed",
          hasSuffix && [
            "flex rounded-(--input-radius) border transition-colors",
            "shadow-(--input-shadow)",
            "outline-1 -outline-offset-1 outline-transparent",
            "bg-(--input-bg) border-(--input-border)",
            !isError && !isNonEditable && "hover:border-(--input-border-hover)",
            !isError && "focus-within:outline-[1.5px] focus-within:-outline-offset-[1.5px] focus-within:outline-(--input-border-focus)",
            isError && "border-(--input-border-error)",
            isError && "focus-within:outline-[1.5px] focus-within:-outline-offset-[1.5px] focus-within:outline-(--input-border-error)",
            isNonEditable && "bg-(--input-bg-disabled)",
          ],
        )}>
          {/* Leading icon */}
          {leadingIcon && (
            <div
              className={cn(
                "absolute top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center",
                currentSize.iconLeft,
                currentSize.icon,
                isError
                  ? "text-(--input-icon-error)"
                  : "text-(--input-icon)",
                isNonEditable && "opacity-50"
              )}
              aria-hidden="true"
            >
              {leadingIcon}
            </div>
          )}

          {/* Input element */}
          <input
            ref={ref}
            id={inputId}
            type={type}
            disabled={disabled}
            readOnly={readOnly}
            maxLength={maxLength}
            value={value}
            aria-invalid={isError}
            aria-describedby={hasHelperOrError ? helperId : undefined}
            className={cn(
              "w-full transition-colors",
              currentSize.input,
              paddingClasses,
              "text-(--input-text)",

              isError
                ? "placeholder:text-(--input-text-placeholder-error)"
                : "placeholder:text-(--input-text-placeholder)",

              // When suffix: input is borderless; container owns the chrome
              hasSuffix
                ? "flex-1 min-w-0 border-0 bg-transparent shadow-none rounded-l-(--input-radius) outline-none focus:outline-none"
                : [
                    "rounded-(--input-radius) border shadow-(--input-shadow)",
                    "outline-1 -outline-offset-1 outline-transparent",
                    "bg-(--input-bg) border-(--input-border)",
                    !isError && !isNonEditable && "hover:border-(--input-border-hover)",
                    !isError && "focus:outline-[1.5px] focus:-outline-offset-[1.5px] focus:outline-(--input-border-focus)",
                    isError && "border-(--input-border-error)",
                    isError && "focus:outline-[1.5px] focus:-outline-offset-[1.5px] focus:outline-(--input-border-error)",
                    isNonEditable && "bg-(--input-bg-disabled)",
                  ],

              isNonEditable && [
                "text-(--input-text-disabled)",
                "cursor-not-allowed",
              ],
              
              className
            )}
            {...props}
          />

          {/* Trailing icon */}
          {trailingIcon && (
            <div
              className={cn(
                "absolute top-1/2 -translate-y-1/2 pointer-events-none flex items-center justify-center",
                currentSize.iconRight,
                currentSize.icon,
                isError
                  ? "text-(--input-icon-error)"
                  : "text-(--input-icon)",
                isNonEditable && "opacity-50"
              )}
              aria-hidden="true"
            >
              {trailingIcon}
            </div>
          )}

          {/* Suffix box */}
          {hasSuffix && (
            <span
              className={cn(
                "flex items-center shrink-0 border-l border-(--input-border) px-3",
                "body-sm-regular text-text-tertiary pointer-events-none select-none",
                isNonEditable && "opacity-50"
              )}
              aria-hidden="true"
            >
              {suffix}
            </span>
          )}
        </div>

        {inlineMeta ? (
          (showCounter || hasHelperOrError) && (
            <div className="flex items-start justify-between gap-2">
              <p
                id={helperId}
                className={cn(
                  "body-xs-regular",
                  isError
                    ? "text-(--input-helper-error)"
                    : "text-(--input-helper)",
                  disabled && "opacity-50"
                )}
              >
                {hasHelperOrError ? (isError && errorMessage ? errorMessage : helperText) : ''}
              </p>
              {showCounter && (
                <p className={cn("body-xs-regular text-right shrink-0", counterColor)}>
                  {charCount}/{maxLength}
                </p>
              )}
            </div>
          )
        ) : (
          <>
            {/* Character count */}
            {showCounter && (
              <p className={cn("body-xs-regular text-right", counterColor)}>
                {charCount}/{maxLength}
              </p>
            )}

            {/* Helper text / Error message */}
            {hasHelperOrError && (
              <p
                id={helperId}
                className={cn(
                  "body-xs-regular",
                  isError
                    ? "text-(--input-helper-error)"
                    : "text-(--input-helper)",
                  disabled && "opacity-50"
                )}
              >
                {isError && errorMessage ? errorMessage : helperText}
              </p>
            )}
          </>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export { Input };
